import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import {
  FaDatabase,
  FaPowerOff,
  FaUser,
  FaChevronDown,
  FaChartLine,
  FaFileAlt,
  FaReceipt,
  FaFileInvoiceDollar,
  FaBook,
  FaUserCog,
  FaChartBar,
  FaClipboardList,
  FaBuilding,
  FaKey,
  FaCalculator,
  FaEnvelope,
} from "react-icons/fa";
import companyLogo from "../Assets/logo3.jpg";
import { FINANCE_MENU_ITEMS } from "./FinanceSidebar";
import { FINANCE_PORTAL_LINKS } from "../constants/financePortalLinks";
import axiosInstance from "../config/axios";
import { toast } from "react-toastify";
import { clearAllAuthSession } from "../utils/appAuth";
import { useRole } from "../context/RoleContext";
import ApsReviewNotifications from "./ApsReviewNotifications";

function menuLabel(title) {
  return String(title || "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bHst\b/g, "HST")
    .replace(/\bEft\b/g, "EFT")
    .replace(/\bT4a\b/g, "T4A")
    .replace(/\bA\/P\b/gi, "A/P")
    .replace(/\bA\/R\b/gi, "A/R");
}

const REPORT_MENU_GROUPS = [
  "Reports",
  "Journals",
  "Trust & Payments",
  "Payroll",
  "Reconciling",
];

const FINANCIAL_REPORT_LINKS = [
  {
    id: "hst",
    title: "HST Report",
    path: "/financial-reports-hst",
    icon: FaReceipt,
    description: "HST collected and input tax credit",
  },
  {
    id: "disbursements",
    title: "Disbursement Journal",
    path: "/financial-reports-disbursements",
    icon: FaFileAlt,
    description: "Disbursement journal for the period",
  },
  {
    id: "trust",
    title: "Trust Journal",
    path: "/financial-reports-trust",
    icon: FaBook,
    description: "Real estate trust journal",
  },
  {
    id: "trade-ar",
    title: "Trade A/R Journal",
    path: "/financial-reports-trade-ar",
    icon: FaChartLine,
    description: "Trade accounts receivable journal",
  },
  {
    id: "agent-payment",
    title: "Agent Payment Report",
    path: "/financial-reports-agent-payment",
    icon: FaUserCog,
    description: "Agent payment details",
  },
  {
    id: "agent-payment-summary",
    title: "Agent Summary Report",
    path: "/financial-reports-agent-payment-summary",
    icon: FaChartBar,
    description: "Agent payment summary",
  },
  {
    id: "activity-details",
    title: "Activity Details Report",
    path: "/financial-reports-activity-details",
    icon: FaClipboardList,
    description: "Commission activity by G/L",
  },
  {
    id: "open-trades-commission",
    title: "Open Trades Commission Report",
    path: "/financial-reports-open-trades-commission",
    icon: FaChartBar,
    description: "Commission for trades opened in a period",
  },
  {
    id: "commission",
    title: "Commission Report",
    path: "/financial-reports-commission",
    icon: FaChartBar,
    description: "Commission by agent and classification",
  },
  {
    id: "pending-trades",
    title: "Pending Trade Report",
    path: "/financial-reports-pending-trades",
    icon: FaClipboardList,
    description: "Pending listing and selling ends",
  },
  {
    id: "custom-report",
    title: "Custom Report",
    path: "/financial-reports-custom",
    icon: FaClipboardList,
    description: "Editable trade report by closing-date period",
  },
  {
    id: "invoice-detail-report",
    title: "Invoice Detail Report",
    path: "/financial-reports-invoice-detail",
    icon: FaClipboardList,
    description: "Monthly invoice detail by entry date",
  },
];

const ACCOUNTING_LINKS = [
  {
    id: "office-deductions",
    title: "Office Deductions",
    path: "/accounting/office-deductions",
    icon: FaCalculator,
    description: "Office expense deductions by trade",
  },
];

const AGENT_REPORT_LINKS = [
  {
    id: "hst",
    title: "HST Report ( Office Share)",
    path: "/financial-reports-hst",
    icon: FaReceipt,
    description: "Your HST for the selected period",
  },
  {
    id: "pending-trades",
    title: "Pending Trade Report",
    path: "/financial-reports-pending-trades",
    icon: FaClipboardList,
    description: "Your pending trades",
  },
  {
    id: "agent-payment",
    title: "Agent Payment Report",
    path: "/financial-reports-agent-payment",
    icon: FaUserCog,
    description: "Your commission payouts",
  },
];

const PAYROLL_LINKS = [
  {
    id: "eft-reports",
    title: "EFT Reports",
    path: null,
    icon: FaFileInvoiceDollar,
    description: "Agent Summary or Total Details",
    action: "eft-report-picker",
  },
  {
    id: "t4a",
    title: "T4A",
    path: "/payroll/t4a",
    icon: FaFileAlt,
    description: "T4A slip",
  },
  {
    id: "t4a-summary",
    title: "T4A Summary",
    path: "/payroll/t4a-summary",
    icon: FaClipboardList,
    description: "T4A summary (office only)",
    officeOnly: true,
  },
];

function DropdownItem({ item, onSelect, label }) {
  const Icon = item.icon;
  const className =
    "w-full text-left flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-gray-50";
  const body = (
    <>
      {Icon && (
        <Icon className="mt-0.5 text-gray-500 flex-shrink-0 text-sm" />
      )}
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-black leading-snug">
          {label}
        </span>
        {item.description && (
          <span className="block text-xs text-gray-500 leading-snug mt-0.5">
            {item.description}
          </span>
        )}
      </span>
    </>
  );

  if (item.path) {
    return (
      <Link
        to={item.path}
        className={className}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(item);
        }}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect?.(item)}
      className={className}
    >
      {body}
    </button>
  );
}

function Navbar({ user: userProp }) {
  const navigate = useNavigate();
  const { isAdmin, isSuperUser, isAgent, isBroker, isFinance, appUser, firebaseUser } = useRole();
  const isOfficeUser = isAdmin || isSuperUser;
  const [isFinanceOpen, setIsFinanceOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isAccountingOpen, setIsAccountingOpen] = useState(false);
  const [isPayrollOpen, setIsPayrollOpen] = useState(false);
  const [isEftReportPickerOpen, setIsEftReportPickerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(userProp || null);
  const [pendingPasswordCount, setPendingPasswordCount] = useState(0);
  const financeMenuRef = useRef(null);
  const reportsMenuRef = useRef(null);
  const accountingMenuRef = useRef(null);
  const payrollMenuRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setCurrentUser(u || userProp || null);
    });
    return () => unsubscribe();
  }, [userProp]);

  useEffect(() => {
    if (!isAdmin) {
      setPendingPasswordCount(0);
      return;
    }
    let cancelled = false;
    axiosInstance
      .get("/auth/password-reset-requests?status=pending")
      .then((res) => {
        if (!cancelled) {
          setPendingPasswordCount(res.data.pendingCount || 0);
        }
      })
      .catch(() => {
        if (!cancelled) setPendingPasswordCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !financeMenuRef.current?.contains(event.target) &&
        !reportsMenuRef.current?.contains(event.target) &&
        !accountingMenuRef.current?.contains(event.target) &&
        !payrollMenuRef.current?.contains(event.target)
      ) {
        setIsFinanceOpen(false);
        setIsReportsOpen(false);
        setIsAccountingOpen(false);
        setIsPayrollOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const displayUser = currentUser || userProp || firebaseUser;
  const userLabel = displayUser
    ? displayUser.displayName || displayUser.email || "Signed in"
    : appUser?.displayName || appUser?.email || null;

  const handleSignOut = async () => {
    try {
      const entryId = sessionStorage.getItem("accessLogEntryId");
      if (entryId) {
        try {
          await axiosInstance.patch(`/access-log/${entryId}/leave`);
        } catch (_) {}
        sessionStorage.removeItem("accessLogEntryId");
      }
      await signOut(auth).catch(() => {});
      clearAllAuthSession();
      navigate("/");
      window.location.reload();
    } catch (error) {
      toast.error("Sign-out error");
    }
  };

  const handleFinanceItemClick = (item) => {
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
    setIsAccountingOpen(false);
    setIsPayrollOpen(false);
    if (item?.action === "eft-report-picker") {
      setIsEftReportPickerOpen(true);
      return;
    }
    if (item?.path) navigate(item.path);
  };

  const handleReportItemClick = (item) => {
    setIsReportsOpen(false);
    setIsFinanceOpen(false);
    setIsAccountingOpen(false);
    setIsPayrollOpen(false);
    if (item?.path) navigate(item.path);
  };

  const closeMenus = () => {
    setIsFinanceOpen(false);
    setIsReportsOpen(false);
    setIsAccountingOpen(false);
    setIsPayrollOpen(false);
  };

  const handlePayrollItemClick = (item) => {
    setIsPayrollOpen(false);
    setIsReportsOpen(false);
    setIsAccountingOpen(false);
    setIsFinanceOpen(false);
    if (item?.action === "eft-report-picker") {
      setIsEftReportPickerOpen(true);
      return;
    }
    if (item?.path) navigate(item.path);
  };

  const selectEftReport = (type) => {
    setIsEftReportPickerOpen(false);
    navigate(`/payroll/eft-reports?type=${type}`);
  };

  return (
    <>
      <nav className="app-nav bg-white text-gray-800 px-6 py-3 flex justify-between items-center shadow-lg relative z-40 overflow-visible">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={isFinance ? "/finance-portal" : "/"} className="flex-shrink-0">
            <img
              src={companyLogo}
              alt="Company Logo"
              className="h-14 w-auto object-contain"
              style={{ maxWidth: "140px" }}
            />
          </Link>
          {isFinance && (
            <div className="flex items-center gap-1 ml-6">
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/finance-portal");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Reports Home
              </button>
              <div className="relative" ref={reportsMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsFinanceOpen(false);
                    setIsAccountingOpen(false);
                    setIsReportsOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isReportsOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Finance Reports
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isReportsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isReportsOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                    {FINANCE_PORTAL_LINKS.map((item) => (
                      <DropdownItem
                        key={item.id}
                        item={item}
                        label={item.title}
                        onSelect={(link) => {
                          setIsReportsOpen(false);
                          navigate(link.path);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {isAgent && (
            <div className="flex items-center gap-1 ml-6">
              <button
                type="button"
                onClick={() => navigate("/listing-info")}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Listing Info
              </button>
              <button
                type="button"
                onClick={() => navigate("/trade-info")}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Trade Info
              </button>
              <button
                type="button"
                onClick={() => navigate("/agent-trade-status")}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Trade Status
              </button>
              <div className="relative" ref={reportsMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setIsAccountingOpen(false);
                  setIsPayrollOpen(false);
                  setIsReportsOpen((open) => !open);
                }}
                className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isReportsOpen
                    ? "bg-gray-100 text-blue-900"
                    : "text-black hover:text-blue-900 hover:bg-gray-100"
                }`}
              >
                Reports
                <FaChevronDown
                  className={`text-xs transition-transform ${
                    isReportsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isReportsOpen && (
                <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                  {AGENT_REPORT_LINKS.map((item) => (
                    <DropdownItem
                      key={item.id}
                      item={item}
                      label={item.title}
                      onSelect={(link) => {
                        setIsReportsOpen(false);
                        navigate(link.path);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={accountingMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setIsReportsOpen(false);
                  setIsPayrollOpen(false);
                  setIsAccountingOpen((open) => !open);
                }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isAccountingOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Accounting
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isAccountingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isAccountingOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                    {ACCOUNTING_LINKS.map((item) => (
                      <DropdownItem
                        key={item.id}
                        item={item}
                        label={item.title}
                        onSelect={(link) => {
                          setIsAccountingOpen(false);
                          navigate(link.path);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="relative" ref={payrollMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsReportsOpen(false);
                    setIsAccountingOpen(false);
                    setIsPayrollOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isPayrollOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Payroll
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isPayrollOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isPayrollOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                    {PAYROLL_LINKS.filter(
                      (item) => !item.officeOnly || isOfficeUser,
                    ).map((item) => (
                      <DropdownItem
                        key={item.id}
                        item={item}
                        label={item.title}
                        onSelect={handlePayrollItemClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {isBroker && (
            <div className="flex items-center gap-1 ml-6">
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/listing-info?tab=documents");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Listing Info
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/trade-info?tab=documents");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Trade Info
              </button>
              <div className="relative" ref={reportsMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsAccountingOpen(false);
                    setIsReportsOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isReportsOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Finance Reports
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isReportsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isReportsOpen && (
                  <div className="absolute left-0 top-full mt-2 w-[640px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-6 z-50">
                    <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-3">
                      Reports
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {FINANCIAL_REPORT_LINKS.map((item) => (
                        <DropdownItem
                          key={item.id}
                          item={item}
                          label={item.title}
                          onSelect={handleReportItemClick}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={accountingMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsReportsOpen(false);
                    setIsAccountingOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isAccountingOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Accounting
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isAccountingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isAccountingOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                    {ACCOUNTING_LINKS.map((item) => (
                      <DropdownItem
                        key={item.id}
                        item={item}
                        label={item.title}
                        onSelect={(link) => {
                          setIsAccountingOpen(false);
                          navigate(link.path);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {isOfficeUser && (
            <div className="flex items-center gap-1 ml-6">
              <div className="relative" ref={financeMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsReportsOpen(false);
                    setIsAccountingOpen(false);
                    setIsFinanceOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isFinanceOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Finance
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isFinanceOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isFinanceOpen && (
                  <div className="absolute left-0 top-full mt-2 w-[1100px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-6 z-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                      {REPORT_MENU_GROUPS.map((group) => (
                        <div key={group}>
                          <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-3">
                            {group}
                          </p>
                          <div className="space-y-1">
                            {FINANCE_MENU_ITEMS.filter(
                              (item) =>
                                item.group === group &&
                                (!item.officeOnly || isOfficeUser),
                            ).map((item) => (
                              <DropdownItem
                                key={item.id}
                                item={item}
                                label={menuLabel(item.title)}
                                onSelect={handleFinanceItemClick}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={reportsMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsFinanceOpen(false);
                    setIsAccountingOpen(false);
                    setIsReportsOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isReportsOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Finance Reports
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isReportsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isReportsOpen && (
                  <div className="absolute left-0 top-full mt-2 w-[640px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl p-6 z-50">
                    <p className="text-[11px] font-semibold tracking-wider text-gray-400 uppercase mb-3">
                      Reports
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {FINANCIAL_REPORT_LINKS.map((item) => (
                        <DropdownItem
                          key={item.id}
                          item={item}
                          label={item.title}
                          onSelect={handleReportItemClick}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={accountingMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setIsFinanceOpen(false);
                    setIsReportsOpen(false);
                    setIsAccountingOpen((open) => !open);
                  }}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isAccountingOpen
                      ? "bg-gray-100 text-blue-900"
                      : "text-black hover:text-blue-900 hover:bg-gray-100"
                  }`}
                >
                  Accounting
                  <FaChevronDown
                    className={`text-xs transition-transform ${
                      isAccountingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isAccountingOpen && (
                  <div className="absolute left-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-2xl p-3 z-50">
                    {ACCOUNTING_LINKS.map((item) => (
                      <DropdownItem
                        key={item.id}
                        item={item}
                        label={item.title}
                        onSelect={(link) => {
                          setIsAccountingOpen(false);
                          navigate(link.path);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/listing-info");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Listing Info
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/trade-info");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Trade Info
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/agent-info");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Agent Info
              </button>
              <button
                type="button"
                onClick={() => {
                  closeMenus();
                  navigate("/vendor-info");
                }}
                className="px-3 py-2 rounded-md text-sm font-medium text-black hover:text-blue-900 hover:bg-gray-100 transition-colors"
              >
                Vendor Info
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3 flex-shrink-0">
          {isOfficeUser && (
            <button
              type="button"
              onClick={() => navigate("/invoice-sent")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title="Invoice Sent Tracker"
            >
              <FaReceipt className="text-lg" />
            </button>
          )}
          {isOfficeUser && (
            <button
              type="button"
              onClick={() => navigate("/trust-mail-sent")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title="Trust Mail Tracker"
            >
              <FaFileInvoiceDollar className="text-lg" />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/company-info")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title="Company Profile"
            >
              <FaBuilding className="text-lg" />
            </button>
          )}
          {(isOfficeUser || isAgent) && <ApsReviewNotifications />}
          {isOfficeUser && (
            <button
              type="button"
              onClick={() => navigate("/inbox")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title="Office Inbox"
            >
              <FaEnvelope className="text-lg" />
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/user-access")}
              className="relative flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title={
                pendingPasswordCount
                  ? `${pendingPasswordCount} password request(s)`
                  : "User Access"
              }
            >
              <FaKey className="text-lg" />
              {pendingPasswordCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] leading-[18px] font-bold">
                  {pendingPasswordCount}
                </span>
              )}
            </button>
          )}
          {isOfficeUser && userLabel && (
            <button
              type="button"
              onClick={() => navigate("/access-log")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title={`Signed in as ${userLabel} • View who has accessed the system`}
            >
              <FaUser className="text-lg" />
            </button>
          )}
          {isOfficeUser && (
            <button
              type="button"
              onClick={() => navigate("/database")}
              className="flex items-center justify-center text-blue-900 bg-gray-100 p-2 rounded-md border border-gray-200 hover:bg-gray-200 transition-colors"
              title="Database"
            >
              <FaDatabase className="text-lg" />
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="bg-gray-600 hover:bg-blue-900 text-white px-4 py-2 rounded-md transition-colors duration-200 flex items-center space-x-2"
            title="Sign Out"
          >
            <FaPowerOff className="text-lg" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </nav>

      {isEftReportPickerOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
          onClick={() => setIsEftReportPickerOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Select EFT report
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Data is always for January–December of the current year.
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => selectEftReport("agent-summary")}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-800 hover:bg-blue-50 text-sm font-medium text-gray-800"
              >
                Agent Summary
              </button>
              <button
                type="button"
                onClick={() => selectEftReport("total-details")}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-800 hover:bg-blue-50 text-sm font-medium text-gray-800"
              >
                Total Details
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsEftReportPickerOpen(false)}
              className="mt-4 w-full px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
